/**
 * Core: Sentry
 * ------------------------------------------------------------------------------
 * Sets up Sentry error and performance monitoring.
 *
 * @namespace Sentry
 */
import { v4 as uuidv4 } from 'uuid'
import * as Sentry from '@sentry/browser'

import { storageNamespace } from '~/helpers/storage-namespaces'

window.mint = window.mint || {}

/**
 * Export new Sentry object.
 */
export default () => {
  let customerId = false

  const settings = mint.services.sentry

  /**
   * Initialise.
   */
  function init() {
    setCustomerId()

    /**
     * Do not enable Sentry if:
     * - DSN variable is not set
     * - The DSN is set, but the feature flag has been specifically disabled.
     * - The Customiser is open.
     */
    if (
      settings.dsn === 'NULL' ||
      mint.request.designMode
    ) {
      return
    }

    const config = {
      dsn: settings.dsn,
      environment: mint.env.mode,
      release: `${mint.env.release}`,
      integrations: [],
      debug: false,
    }

    /**
     * Disable additional integrations until usage allowance is reconciled.
     */
    // const integrations = getIntegrations()
    config.denyUrls = getDeniedUrlPatterns()
    config.ignoreErrors = getIgnoredErrorPatterns()

    /**
     * Sample rate requires re-deployment to change.
     * - This configures what % of user errors are reported to Sentry.
     * - Can be found in Buddy environment variables.
     * - Default to 1 as localhost will not have this variable.
     */
    // eslint-disable-next-line no-undef
    config.sampleRate = parseInt(`${__SENTRY_SAMPLE_RATE__}`) || 1

    /**
     * Traces Sample Rate.
     * - This reports what % of transactions are reported to
     * Sentry for performance monitoring.
     * - https://docs.sentry.io/platforms/javascript/performance/#configure
     */
    config.tracesSampleRate = parseFloat(settings.tracesSampleRate) || 0.1

    /**
     * Session Replay Sample Rates
     * - https://docs.sentry.io/platforms/javascript/configuration/integrations/replay/
     * - What % of overall sessions are recorded for replay.
     * - What % of errors are recorded for replay.
     */
    config.replaysSessionSampleRate = parseFloat(settings.replaysSessionSampleRate) || 0.1
    config.replaysOnErrorSampleRate = parseFloat(settings.replaysOnErrorSampleRate) || 0.1

    /**
     * Initialise Sentry.
     * - https://docs.sentry.io/platforms/javascript/configuration/options/
     */
    Sentry.init(config)

    initialiseIntegrations()

    /**
     * Customer information.
     * - https://docs.sentry.io/platforms/javascript/enriching-events/identify-user/
     */
    const preferredLanguage = navigator.languages[0] || navigator.language

    Sentry.setUser({
      'id': customerId,
      'language': preferredLanguage,
      'logged_in': mint.customer.loggedIn,
      'shopify_id': mint.customer.id,
    })

    navigator.languages

    /**
     * Additional context.
     * - https://docs.sentry.io/platforms/javascript/enriching-events/context/
     */
    Sentry.setContext('Shopify', {
      'theme_name': mint.theme.name,
    })

    /**
     * Tags.
     * - Generally more powerful enrichment as they have stats.
     * - https://docs.sentry.io/platforms/javascript/enriching-events/tags/
     */
    Sentry.setTag('shopify.market', mint.shop.country)
    Sentry.setTag('shopify.page_type', mint.request.pageType)
  }

  /**
   * Initialise additional Sentry integrations.
   * - Uses lazyload method to reduce bundle size.
   * - https://docs.sentry.io/platforms/javascript/configuration/integrations/#1-dynamic-import-recommended
   */
  function initialiseIntegrations() {
    const {
      enableBrowserTracing,
      enableCaptureConsole,
      enableSessionReplay,
    } = mint.flags.sentry

    import(
      /* webpackChunkName: 'vendor.sentry-lazy' */
      '@sentry/browser').then((lazyLoadedSentry) => {

      /**
       * captureConsole
       * - https://docs.sentry.io/platforms/javascript/configuration/integrations/captureconsole/
       */
      if (enableCaptureConsole) {
        Sentry.addIntegration(lazyLoadedSentry.captureConsoleIntegration()({
          levels: [
            'assert',
            'error',
          ],
        }))
      }

      /**
       * Browser Tracing.
       * - Lazy-loading may impact perf monitoring unless request is cached
       *   by the browser.
       * - TODO: Check the above.
       * - https://docs.sentry.io/platforms/javascript/performance/
       */
      if (enableBrowserTracing) {
        Sentry.addIntegration(lazyLoadedSentry.browserTracingIntegration())
      }

      /**
       * Session Replay.
       * - https://docs.sentry.io/platforms/javascript/configuration/integrations/replay/
       */
      if (enableSessionReplay) {
        Sentry.addIntegration(lazyLoadedSentry.replayIntegration())
      }
    })
  }

  /**
   * Parse regex string from the theme into a usable regex array.
   * Regex over exact string as this allows for partial-matching in Sentry.
   * - https://docs.sentry.io/platforms/javascript/configuration/integrations/inboundfilters/#denyurls
   * @returns {Array}
   */
  function getDeniedUrlPatterns() {
    const deniedUrls = settings.denyUrls.split(',') || []

    const formattedUrls = deniedUrls.map((url) => {
      if (!url.length) {
        return false
      }

      return new RegExp(url)
    }).filter(Boolean)

    return formattedUrls
  }

  /**
   * Parse substring matches from the theme into a usable array.
   * - Filter out potential empty items to reduce risk.
   * - https://docs.sentry.io/platforms/javascript/configuration/integrations/inboundfilters/#denyurls
   * @returns {Array}
   */
  function getIgnoredErrorPatterns() {
    const ignoredErrorList = settings.ignoredErrors.split(',') || []

    const formattedIgnoredErrors = ignoredErrorList.map((error) => {
      if (!error.length) {
        return false
      }

      return error
    }).filter(Boolean)

    return formattedIgnoredErrors
  }

  /**
   * Sets a globally available customer ID.
   * - This ID can/will be used for other services (e.g. Attraqt tracking).
   * @returns {String}
   */
  function setCustomerId() {
    const storedId = localStorage.getItem(storageNamespace.customerId)

    if (storedId) {
      customerId = storedId
      return
    }

    const uniqueId = uuidv4()

    localStorage.setItem(storageNamespace.customerId, uniqueId)

    customerId = uniqueId
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}